import { ChangeEvent } from 'react';

import { cn } from '@utils/cn';

import { Checkbox, HelperText, TwSelector as Selector, Text } from '@ui-component-library';

import { useCheckout } from '@components/Checkout/context/CheckoutContext';

const options = [
  { label: 'Nej', value: false },
  { label: 'Ja', value: true }
];

export const ObstacleInfo = () => {
  const { extraData, setExtraData, orderStepFieldErrors, setOrderStepFieldErrors } = useCheckout();

  const hasObstacles = extraData.hasObstacles as boolean | undefined;

  const handleSelect = (option: { value: string }) => {
    setExtraData((prev) => ({ ...prev, hasObstacles: option.value === 'true' }));
    setOrderStepFieldErrors((prev) => ({ ...prev, obstacleInfo: '' }));
  };

  const handleChange = (value: unknown, name: string) => {
    setOrderStepFieldErrors((prev) => ({ ...prev, [name]: '' }));
    setExtraData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Text variant="text-base/leading-normal/medium">Hjälp piloten planera flygningen</Text>
        <Text variant="text-sm/leading-normal/normal" className="text-pebble-500">
          Ange om det finns hinder som bommar eller svårtillgängliga vägar. Alla fält är
          obligatoriska.
        </Text>
      </div>

      <div className="h-[0.5px] w-full border border-stone-500/17" />

      <div className="flex flex-col gap-2 px-4 pt-2">
        <Text variant="text-sm/leading-normal/medium">Finns det några hinder för piloten?</Text>
        <Text variant="text-sm/leading-normal/normal" className="text-pebble-500">
          T.ex. vägbommar, grindar eller svårframkomlig väg.
        </Text>
      </div>

      <div className="flex w-full flex-col gap-1.5">
        <Selector>
          {options.map((option) => (
            <Selector.Option
              key={option.value.toString()}
              label={option.label}
              value={option.value.toString()}
              isSelected={hasObstacles === option.value}
              onClick={handleSelect}
            />
          ))}
        </Selector>
        <HelperText
          icon="ExclamationFilled"
          label={orderStepFieldErrors.obstacleInfo}
          className={cn(
            'fill-danger-500 text-danger-500 pl-4',
            orderStepFieldErrors.obstacleInfo ? 'opacity-100' : 'opacity-0'
          )}
        />
      </div>

      {hasObstacles && (
        <>
          <div className="flex flex-col gap-2 px-4 pt-2">
            <Text variant="text-sm/leading-normal/medium">Hur kan piloten passera hindret?</Text>
            <Text variant="text-sm/leading-normal/normal" className="text-pebble-500">
              Ange t.ex. kod till bom, kontaktperson eller annan instruktion som hjälper piloten att
              passera.
            </Text>
          </div>
          <textarea
            value={(extraData.obstacleDescription as string) ?? ''}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
              handleChange(e.target.value, 'obstacleDescription')
            }
            placeholder="Lägg till kod, kontaktupgift eller alternativ väg"
            className="bg-white-50 text-charcoal-500 placeholder:text-pebble-400 focus:border-charcoal-500 min-h-[100px] w-full resize-none rounded-xl border border-stone-500/17 p-3 text-sm leading-normal focus:outline-none"
          />
        </>
      )}

      <div className="h-[0.5px] w-full border border-stone-500/17" />

      <div className="flex flex-col gap-4">
        <Text variant="text-sm/leading-normal/medium" className="px-4 pt-2">
          Bekräfta innan du går vidare
        </Text>
        <div className="flex flex-col gap-1">
          <div className="flex flex-col gap-1.5">
            <div className="bg-white-50 flex flex-col gap-2 rounded-lg p-4">
              <Checkbox
                name="ownershipTerms"
                onClick={handleChange}
                checked={extraData.ownershipTerms}
              >
                <Text variant="text-sm/leading-normal/normal">
                  Jag äger fastigheten eller har tillåtelse från fastighetsägaren att beställa
                  tjänsten
                </Text>
              </Checkbox>
            </div>
            <HelperText
              icon="ExclamationFilled"
              label={orderStepFieldErrors.ownershipTerms}
              className={cn(
                'fill-danger-500 text-danger-500 pb-2 pl-4',
                orderStepFieldErrors.ownershipTerms ? 'opacity-100' : 'opacity-0'
              )}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="bg-white-50 flex flex-col gap-2 rounded-lg p-4">
              <Checkbox
                name="locationTerms"
                onClick={handleChange}
                checked={extraData.locationTerms}
              >
                <Text variant="text-sm/leading-normal/normal">
                  Min fastighet ligger inte på en ö eller på militärt område
                </Text>
              </Checkbox>
            </div>
            <HelperText
              icon="ExclamationFilled"
              label={orderStepFieldErrors.locationTerms}
              className={cn(
                'fill-danger-500 text-danger-500 pb-2 pl-4',
                orderStepFieldErrors.locationTerms ? 'opacity-100' : 'opacity-0'
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
